import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import { Game } from '../services/backendService/types';
import { newRelicNoticeError } from './useNewRelic';

const STALE_TIME = 10 * 60 * 1000; // 10 minutes

function useGameListener(gameId: string, tenantId: string) {
  const { gameAPI } = useContext(SocketContext);
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => ['game-listener', tenantId, gameId], [tenantId, gameId]);

  // Subscribe to real-time updates and populate the query cache
  useEffect(() => {
    if (!tenantId || !gameId || !gameAPI) return;

    const sub = gameAPI.game.onChange({ gameId, tenantId }).subscribe({
      next: val => {
        queryClient.setQueryData<Game>(queryKey, val);
      },
      error: error => {
        newRelicNoticeError(error, 'Error while listening to game changes');
      },
    });

    return () => {
      sub.unsubscribe();
    };
  }, [tenantId, gameId, gameAPI, queryClient, queryKey]);

  const { data: game } = useQuery<Game | undefined>({
    queryKey,
    queryFn: () => Promise.resolve(undefined),
    staleTime: STALE_TIME,
    enabled: Boolean(tenantId && gameId && gameAPI),
  });

  return {
    game,
  };
}

export default useGameListener;
